import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, TextField } from '@mui/material';
import { AddPhotoAlternate, Close, DeleteOutline } from '@mui/icons-material';
import { getAbsoluteFileUrl } from 'src/app/helpers/utils';
import { useEffect, useState } from 'react';
import useMessage from 'src/app/hooks/useMessage';
import {
  useAddSkillMutation,
  useGetSkillByIdQuery,
  useUpdateSkillMutation,
} from 'src/app/services/skillApi';
import { Controller, useForm } from 'react-hook-form';
import handleError from '@fuse/core/errorHandler';
import { createPortal } from 'react-dom';

const SkillAddEditDialog = ({ skill, isOpen, onClose, refetch, onUpdate }) => {
  const { t } = useTranslation('translations');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [addSkill, { isLoading }] = useAddSkillMutation();
  const [updateSkill, { isLoading: isEditLoading }] = useUpdateSkillMutation();
  const { data: skillData } = useGetSkillByIdQuery(skill?.id, {
    skip: !skill?.id,
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isValid, errors },
    reset,
    control,
  } = useForm();
  const { showMessage } = useMessage();

  useEffect(() => {
    if (file) {
      clearErrors('file');
    }
  }, [file]);

  useEffect(() => {
    if (skill?.id && skillData) {
      reset({
        nameUz: skillData.nameUz || '',
        nameRu: skillData.nameRu || '',
      });
      setPreview(getAbsoluteFileUrl(skillData?.pictureUrl));
      setFile({
        id: skillData.id,
        src: skillData?.pictureUrl,
      });
    } else if (!skill?.id) {
      reset({
        nameUz: '',
        nameRu: '',
      });
      setPreview(null);
      setFile(null);
    }
  }, [skillData, skill, reset]);

  const handleDialogClose = () => {
    onClose();
  };

  const onSubmit = ({ nameUz, nameRu }) => {
    if (!file) {
      setError('file', {
        type: 'manual',
        message: t('SkillImageIsRequired'),
      });
      return;
    }
    const isEdit = skill?.id > 0;
    const formData = new FormData();
    if (file?.id) {
      formData.append('fileId', file.id);
    } else {
      formData.append('file', file);
    }

    const promise = isEdit
      ? updateSkill({ skillId: skill.id, nameUz: nameUz.trim(), nameRu: nameRu.trim(), formData })
      : addSkill({ nameUz: nameUz.trim(), nameRu: nameRu.trim(), formData });
    promise
      .then((res) => {
        if (res) {
          showMessage({ message: t('SuccessfullySaved'), variant: 'success' });
          refetch();
          handleDialogClose();
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setFile(file);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setFile(null);
    }
  };

  const handleDeleteFile = () => {
    setFile(null);
    setPreview(null);
  };

  return createPortal(
    <Dialog open={isOpen} onClose={handleDialogClose}>
      <DialogTitle className="flex items-center justify-between">
        {skill?.id > 0 ? <p>{t('Edit')}</p> : <p>{t('Create')}</p>}
        <IconButton onClick={handleDialogClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form className="flex flex-col gap-14 mt-6" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="nameUz"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('RequiredField'),
              },
              validate: (value) => value.trim() !== '' || t('RequiredField'),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Title') + ' (uz.)'}
                size="small"
                error={Boolean(errors.nameUz)}
                helperText={errors.nameUz?.message}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => field.onChange(e.target.value.trimStart())}
              />
            )}
          />

          <Controller
            name="nameRu"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('RequiredField'),
              },
              validate: (value) => value.trim() !== '' || t('RequiredField'),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Title') + ' (рус.)'}
                size="small"
                error={Boolean(errors.nameRu)}
                helperText={errors.nameRu?.message}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => field.onChange(e.target.value.trimStart())}
              />
            )}
          />

          {!preview && (
            <div className="flex flex-col gap-2 items-center">
              <Button variant="text" color="success" component="label">
                <AddPhotoAlternate /> {t('AddLogo')}
                <input
                  type="file"
                  hidden
                  accept=".svg,.png,.jpg,.jpeg,.bmp"
                  onChange={handleFileChange}
                />
              </Button>
              {errors?.file?.message && (
                <p className="text-red-600 text-sm">{errors?.file?.message}</p>
              )}
            </div>
          )}
          {preview && (
            <div className="flex justify-center items-center gap-10">
              <img src={preview} alt="Preview" className="w-96 h-96 object-contain mt-2" />
              <DeleteOutline
                className="text-red text-20 ml-10 cursor-pointer"
                onClick={handleDeleteFile}
              />
            </div>
          )}

          <Button
            disabled={isLoading || isEditLoading}
            type="submit"
            className="w-full"
            variant="contained"
            color="secondary"
          >
            <span className="mx-8">{t('Save')}</span>
          </Button>
        </form>
      </DialogContent>
    </Dialog>,
    document.body
  );
};

export default SkillAddEditDialog;
